// OneSignal Web SDK integration.
//
// On native (iOS/Android wrapper), OneSignal's mobile SDK is integrated by
// the build system (e.g. webtoapp.design's pipeline) and handles its own
// init + subscription. This module only handles the web side, where we
// load the OneSignal Web SDK v16 and bind the Supabase user_id as the
// OneSignal external_id so the Edge Function can address users by their
// Supabase id.
//
// The Supabase send functions use `include_aliases: { external_id: [...] }`
// which works for both web (via this SDK call) and native (via the
// equivalent OneSignal.login() call in the native SDK).

import { isNativeApp, isCapacitor } from './platform';

const SDK_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';

let sdkLoaded = false;
let initStarted = false;
let pendingLoginId = null;

/**
 * In our Capacitor wrapper the native OneSignal SDK owns push, so route
 * login/logout/prompt to the native bridge (window.__pmOneSignal, injected by
 * the wrapper) instead of the web SDK. Retries once in case the bridge script
 * hasn't run yet when auth resolves. No-op outside Capacitor.
 */
function bridgeToNative(method, arg) {
  if (typeof window === 'undefined') return;
  const call = () => {
    const b = window.__pmOneSignal;
    if (b && typeof b[method] === 'function') { b[method](arg); return true; }
    return false;
  };
  if (!call()) setTimeout(call, 800);
}

function injectSdkScript() {
  if (sdkLoaded || typeof document === 'undefined') return;
  if (document.querySelector(`script[src="${SDK_URL}"]`)) {
    sdkLoaded = true;
    return;
  }
  const s = document.createElement('script');
  s.src = SDK_URL;
  s.defer = true;
  document.head.appendChild(s);
  sdkLoaded = true;
}

/**
 * Initialize OneSignal Web SDK. Safe to call multiple times — only the
 * first call does anything. No-op on native (wrapper SDK handles it).
 */
export function initOneSignal() {
  if (isNativeApp) return; // native SDK takes over there
  if (initStarted) return;
  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  if (!appId) {
    if (import.meta.env.DEV) console.warn('[OneSignal] VITE_ONESIGNAL_APP_ID not set — push disabled on web');
    return;
  }
  initStarted = true;
  injectSdkScript();

  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async function (OneSignal) {
    try {
      await OneSignal.init({
        appId,
        // Prevent the OneSignal default subscription prompt from firing
        // automatically — we'll trigger it via promptForPush() at a moment
        // that fits our UX (e.g. after the first event registration).
        autoResubscribe: true,
        notifyButton: { enable: false },
        allowLocalhostAsSecureOrigin: true,
      });
      // If someone logged in before the SDK finished loading, apply now.
      if (pendingLoginId) {
        await OneSignal.login(pendingLoginId);
        pendingLoginId = null;
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('[OneSignal] init error:', err);
    }
  });
}

/**
 * Bind the OneSignal subscription to a Supabase user_id. Call this after
 * the user signs in. Safe to call before init completes — queued for
 * after SDK loads.
 */
export function onesignalLogin(userId) {
  if (isCapacitor) { if (userId) bridgeToNative('login', userId); return; } // our wrapper's native SDK
  if (isNativeApp) return; // webtoapp's native SDK handles user binding
  if (!userId) return;
  if (typeof window === 'undefined') return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  pendingLoginId = userId; // remember in case SDK isn't ready yet
  window.OneSignalDeferred.push(async function (OneSignal) {
    try {
      await OneSignal.login(userId);
      pendingLoginId = null;
    } catch (err) {
      if (import.meta.env.DEV) console.error('[OneSignal] login error:', err);
    }
  });
}

/**
 * Unbind the subscription on sign-out so the next user on this device
 * doesn't get the previous user's notifications.
 */
export function onesignalLogout() {
  if (isCapacitor) { bridgeToNative('logout'); return; }
  if (isNativeApp) return;
  if (typeof window === 'undefined') return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  pendingLoginId = null;
  window.OneSignalDeferred.push(async function (OneSignal) {
    try { await OneSignal.logout(); } catch { /* ignore */ }
  });
}

/**
 * Manually prompt the user to allow web push. Tie this to a meaningful
 * moment in the UX (after registering for an event, after starting a
 * route, etc.) rather than on app load.
 */
export function promptForPush() {
  if (isCapacitor) { bridgeToNative('prompt'); return; }
  if (isNativeApp) return;
  if (typeof window === 'undefined') return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async function (OneSignal) {
    try { await OneSignal.Notifications.requestPermission(); }
    catch (err) { if (import.meta.env.DEV) console.error('[OneSignal] permission error:', err); }
  });
}
